import React, { useState, useMemo, useRef } from "react";
import { Row, Col, Card, CardHeader, CardBody, Input, Button, Label, FormGroup, FormFeedback } from "reactstrap";
import { Catalogue } from "../../types/catalogue.types";
import ShareInviteModal from "./ShareInviteModal";
import CatalogueModal from "../../pages/catalog/CatalogueModal";
import PaginationComponent from "../../common/Components/PaginationComponent";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import HKPDrawer from "../../common/Components/HKPDrawer";
import HKPConfirmDialog from "../../common/Components/HKPConfirmDialog";
import StudioAnalyticsSettings from "./StudioAnalyticsSettings";
import { connect } from "react-redux";
import { getCatAlogue } from "../../Store/catelogue/actions";
import notifications from "../../common/Components/Notifications/Notification";
import { debounce } from "../../common/commonFiles/debounce.js";
import {
  isSystemManagedCatalogue,
  mapStudioCatalogueFromApi,
} from "./catalogueSystemManaged";

interface CatalogueListProps {
  onOpenCatalogue: (catalogue: Catalogue) => void;
  getCatAlogue?: (payload: any, callback: (res: any) => void) => void;
  onExportData?: () => void;
  exporting?: boolean;
}

export const CatalogueList: React.FC<CatalogueListProps> = ({
  onOpenCatalogue,
  getCatAlogue,
  onExportData,
  exporting,
}) => {
  // Pagination state
  const [offset, setOffset] = useState<number>(0);
  const [limit] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  
  // Other state
  const [search, setSearch] = useState<string>("");
  const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
  const [selectedCatalogueForShare, setSelectedCatalogueForShare] = useState<Catalogue | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [catalogueToDelete, setCatalogueToDelete] = useState<Catalogue | null>(null);
  const [catalogues, setCatalogues] = useState<Catalogue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const deleteInFlight = useRef(false);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [editingCatalogue, setEditingCatalogue] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>("");
  const [editDescription, setEditDescription] = useState<string>("");
  const [editCategory, setEditCategory] = useState<string>("");
  const [editTargetMarket, setEditTargetMarket] = useState<string>("");
  const [editError, setEditError] = useState("");
  const [editFieldErrors, setEditFieldErrors] = useState<Record<string, string>>({});
  const [editLoading, setEditLoading] = useState(false);
  const [confirmDiscardEdit, setConfirmDiscardEdit] = useState(false);
  const editInFlight = useRef(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);


  // Load catalogues from API with pagination
  const loadCatalogues = (pageOffset?: number, searchTerm?: string) => {
    if (getCatAlogue) {
      setLoading(true);
      const currentOffset = pageOffset !== undefined ? pageOffset : offset;
      const currentSearch = searchTerm !== undefined ? searchTerm : search;
      
      getCatAlogue(
        { 
          offset: currentOffset, 
          limit: limit,
          search: currentSearch || undefined,
          sortBy: "createdAt",
          sortOrder: "desc"
        },
        (res: any) => {
          setLoading(false);
          if (res?.success && res?.data?.docs) {
            const transformedCatalogues = res.data.docs.map((catalogue: any) =>
              mapStudioCatalogueFromApi(catalogue)
            );
            setCatalogues(transformedCatalogues);
            
            // Update pagination info
            if (res.data.pagination) {
              setTotalCount(res.data.pagination.total);
              setTotalPages(Math.ceil(res.data.pagination.total / limit));
            }
          } else {
            notifications({
              title: "Error!",
              message: "Failed to load catalogues",
              type: "danger",
            });
          }
        }
      );
    }
  };


  // Debounced search function
  const debouncedSearch = useMemo(
    () => debounce((searchTerm: string) => {
      setOffset(0); // Reset to first page on search
      loadCatalogues(0, searchTerm);
    }, 500),
    []
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    setSearch(searchTerm);
    debouncedSearch(searchTerm);
  };

  // Load catalogues on component mount
  React.useEffect(() => {
    loadCatalogues();
  }, []);

  const handleCreateCatalogue = () => {
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  const handleCatalogueCreated = () => {
    setCreateModalOpen(false);
    loadCatalogues(); // Refresh the list
  };

  const handleDuplicateCatalogue = async (catalogue: Catalogue) => {
    if (!catalogue?.id || duplicatingId) return;
    try {
      setDuplicatingId(catalogue.id);
      const response = await fetch(
        `${process.env.REACT_APP_SERVERURL}/catalouge/${encodeURIComponent(catalogue.id)}/duplicate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
          },
        }
      );
      const result = await response.json().catch(() => ({}));
      if (response.ok && result?.success) {
        const newName =
          typeof result?.data?.name === "string" && result.data.name.trim()
            ? result.data.name.trim()
            : "copy";
        notifications({
          title: "Duplicated",
          message: `Created "${newName}". Rename it with Edit when you are ready.`,
          type: "success",
        });
        setOffset(0);
        loadCatalogues(0);
      } else {
        notifications({
          title: "Could not duplicate",
          message: result?.message || "Failed to duplicate this catalogue.",
          type: "danger",
        });
      }
    } catch {
      notifications({
        title: "Could not duplicate",
        message: "Failed to duplicate this catalogue.",
        type: "danger",
      });
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleShareCatalogue = (catalogue: Catalogue) => {
    setSelectedCatalogueForShare(catalogue);
    setShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setSelectedCatalogueForShare(null);
  };

  const handleDeleteCatalogue = (catalogue: Catalogue) => {
    setCatalogueToDelete(catalogue);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    if (deleteInFlight.current) return;
    setDeleteModalOpen(false);
    setCatalogueToDelete(null);
  };

  const handleConfirmDelete = async (confirmationCode: string) => {
    if (!catalogueToDelete?.id || deleteInFlight.current) return;

    try {
      deleteInFlight.current = true;
      setDeleteLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_SERVERURL}/catalouge/${catalogueToDelete.id}/delete?confirmationCode=${confirmationCode}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json().catch(() => ({}));

      if (response.ok && result.success) {
        notifications({
          title: "Success!",
          message: "Catalogue deleted successfully",
          type: "success",
        });
        setDeleteModalOpen(false);
        setCatalogueToDelete(null);
        loadCatalogues(); // Refresh the list
      } else {
        notifications({
          title: "Error!",
          message: "Could not delete this catalogue. Please try again.",
          type: "danger",
        });
      }
    } catch (error) {
      notifications({
        title: "Error!",
        message: "Failed to delete catalogue",
        type: "danger",
      });
    } finally {
      deleteInFlight.current = false;
      setDeleteLoading(false);
    }
  };

  const handleEditCatalogue = (catalogue: Catalogue) => {
    setEditError("");
    setEditFieldErrors({});
    setEditingCatalogue(catalogue.id);
    setEditName(catalogue.name);
    setEditDescription(catalogue.description || "");
    setEditCategory(catalogue.category || "");
    setEditTargetMarket(catalogue.targetMarket || "");
  };

  const clearEdit = () => {
    setEditingCatalogue(null);
    setEditName("");
    setEditDescription("");
    setEditCategory("");
    setEditTargetMarket("");
  };

  const handleCancelEdit = () => {
    if (editInFlight.current) return;
    const original = catalogues.find((catalogue) => catalogue.id === editingCatalogue);
    if (original && (
      editName !== original.name ||
      editDescription !== (original.description || "") ||
      editCategory !== (original.category || "") ||
      editTargetMarket !== (original.targetMarket || "")
    )) {
      setConfirmDiscardEdit(true);
      return;
    }
    clearEdit();
  };

  const handleSaveEdit = async (catalogueId: string) => {
    if (!catalogueId || editInFlight.current) return;
    const fieldErrors: Record<string, string> = {};
    if (editName.trim().length < 3 || editName.trim().length > 100) fieldErrors.name = "Name must be 3–100 characters.";
    if (!editDescription.trim() || editDescription.trim().length > 500) fieldErrors.description = "Description is required and must be at most 500 characters.";
    if (editCategory.trim().length > 100) fieldErrors.category = "Category must be at most 100 characters.";
    if (editTargetMarket.trim().length > 200) fieldErrors.targetMarket = "Target market must be at most 200 characters.";
    setEditFieldErrors(fieldErrors);
    if (Object.keys(fieldErrors).length) {
      return;
    }
    editInFlight.current = true;
    setEditLoading(true);
    setEditError("");
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVERURL}/catalouge/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          catalougeId: catalogueId,
          name: editName.trim(),
          description: editDescription.trim(),
          category: editCategory.trim(),
          targetMarket: editTargetMarket.trim()
        })
      });

      const result = await response.json().catch(() => ({}));
      if (response.ok && result?.success === true) {
        notifications({
          title: "Success!",
          message: "Catalogue updated successfully",
          type: "success",
        });
        setEditingCatalogue(null);
        setEditName("");
        setEditDescription("");
        setEditCategory("");
        setEditTargetMarket("");
        loadCatalogues(); // Refresh the list
      } else {
        setEditError("Could not save this catalogue. Please try again.");
      }
    } catch (error) {
      setEditError("Could not save this catalogue. Please try again.");
    } finally {
      editInFlight.current = false;
      setEditLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badgeClass = status === 'active' ? 'bg-success' : 
                      status === 'draft' ? 'bg-warning' : 'bg-secondary';
    return (
      <span className={`badge ${badgeClass} text-white`} style={{ fontSize: "10px" }}>
        {status.toUpperCase()}
      </span>
    );
  };

  return (
    <div>
      {/* Header Section */}
      <div className="catalogue-list-toolbar">
        <div className="catalogue-list-title-row">
          <h2 className="catalogue-list-title">Catalogues</h2>
          <StudioAnalyticsSettings
            className="catalogue-studio-settings--title"
            open={analyticsOpen}
            onOpen={() => setAnalyticsOpen(true)}
            onClose={() => setAnalyticsOpen(false)}
          />
        </div>
        <div className="catalogue-list-toolbar-end">
          <span className="catalogue-env-pill">
            {process.env.REACT_APP_ENV || "loc"}
          </span>
          {onExportData ? (
            <Button
              type="button"
              className="catalogue-toolbar-btn catalogue-toolbar-btn-outline"
              disabled={exporting}
              onClick={onExportData}
            >
              {exporting ? "Exporting…" : "Export Data"}
            </Button>
          ) : null}
          <Input
            type="text"
            placeholder="Search catalogues..."
            value={search}
            onChange={handleSearchChange}
            className={`catalogue-list-search${search ? " border-primary" : ""}`}
          />
          <div className="catalogue-list-create-row">
            <Button
              type="button"
              className="catalogue-toolbar-btn catalogue-toolbar-btn-solid"
              onClick={handleCreateCatalogue}
              disabled={loading}
            >
              {loading ? "Loading..." : "Create Catalogue"}
            </Button>
            <StudioAnalyticsSettings
              className="catalogue-studio-settings--phone"
              open={analyticsOpen}
              onOpen={() => setAnalyticsOpen(true)}
              onClose={() => setAnalyticsOpen(false)}
            />
          </div>
        </div>
      </div>

      {/* Catalogues Table */}
      <Card>
        <CardBody className="p-0">
          <div className="catalogue-data-scroll">
            <div className="catalogue-data-scroll-inner">
          {/* Table Header */}
          <div className="catalogue-list-head px-3 py-2 border-bottom">
                          <Row className="fw-semibold text-muted small align-items-center flex-nowrap">
                            <Col xs={2}>Name & Description</Col>
                            <Col xs={2}>Category & Target Market</Col>
                            <Col xs={2} className="text-center">Created Date & Time</Col>
                            <Col xs={1}>Products</Col>
                            <Col xs={2}>Share links / Opens</Col>
                            <Col xs={1}>Status</Col>
                            <Col xs={2} className="text-end">Actions</Col>
                          </Row>
          </div>

          {/* Table Body */}
          <div>
            {catalogues.map((catalogue) => (
              <div key={catalogue.id} className="px-3 py-3 border-bottom">
                <Row className="align-items-center flex-nowrap">
                  <Col xs={2}>
                    <div>
                        <h6 className="mb-1 fw-bold d-flex align-items-center flex-wrap gap-2">
                          <span>{catalogue.name}</span>
                          {isSystemManagedCatalogue(catalogue) ? (
                            <span className="catalogue-auto-badge">Auto</span>
                          ) : null}
                        </h6>
                        <small className="text-muted">
                          {isSystemManagedCatalogue(catalogue)
                            ? "Updated automatically from product stock"
                            : catalogue.description}
                        </small>
                    </div>
                  </Col>
                  <Col xs={2}>
                    <div>
                        <div className="mb-1">
                          <small className="text-muted">Category:</small>
                          <br />
                          <small className="fw-semibold">{catalogue.category || "Not specified"}</small>
                        </div>
                        <div>
                          <small className="text-muted">Target:</small>
                          <br />
                          <small className="fw-semibold">{catalogue.targetMarket || "Not specified"}</small>
                        </div>
                    </div>
                  </Col>
                          <Col xs={2}>
                            <div className="text-center">
                              <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                                {catalogue.created}
                              </div>
                              <div className="text-muted small" style={{ fontSize: "11px" }}>
                                {catalogue.createdTime}
                              </div>
                            </div>
                          </Col>
                  <Col xs={1}>
                    <span className="fw-bold">{catalogue.products}</span>
                  </Col>
                          <Col xs={2}>
                            <div>
                              <div className="fw-bold text-dark" style={{ fontSize: "14px" }}>
                                {catalogue.shared} share links
                              </div>
                              <div className="text-muted small" style={{ fontSize: "11px" }}>
                                {catalogue.accessed} opens
                              </div>
                            </div>
                          </Col>
                          <Col xs={1}>
                            {getStatusBadge(catalogue.status || 'active')}
                          </Col>
                          <Col xs={2} className="text-end">
                              <div className="catalogue-row-actions">
                                <Button
                                  type="button"
                                  className="btn-cat-action btn-cat-open"
                                  onClick={() => onOpenCatalogue(catalogue)}
                                >
                                  Open
                                </Button>
                                {!isSystemManagedCatalogue(catalogue) ? (
                                  <Button
                                    type="button"
                                    className="btn-cat-action btn-cat-duplicate"
                                    onClick={() => handleDuplicateCatalogue(catalogue)}
                                    disabled={Boolean(duplicatingId)}
                                  >
                                    {duplicatingId === catalogue.id ? "Copying..." : "Duplicate"}
                                  </Button>
                                ) : null}
                                {!isSystemManagedCatalogue(catalogue) ? (
                                  <Button
                                    type="button"
                                    className="btn-cat-action btn-cat-edit"
                                    onClick={() => handleEditCatalogue(catalogue)}
                                  >
                                    Edit
                                  </Button>
                                ) : null}
                                <Button
                                  type="button"
                                  className="btn-cat-action btn-cat-share"
                                  onClick={() => handleShareCatalogue(catalogue)}
                                >
                                  Share
                                </Button>
                                {!isSystemManagedCatalogue(catalogue) ? (
                                  <Button
                                    type="button"
                                    className="btn-cat-action btn-cat-delete"
                                    onClick={() => handleDeleteCatalogue(catalogue)}
                                  >
                                    Delete
                                  </Button>
                                ) : null}
                              </div>
                          </Col>
                </Row>
              </div>
            ))}
          </div>
            </div>
          </div>

          {catalogues.length === 0 && !loading && (
            <div className="text-center py-5">
              <p className="text-muted">No catalogues found matching your search.</p>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Pagination */}
      <PaginationComponent
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPageChange={(newOffset) => {
          setOffset(newOffset);
          loadCatalogues(newOffset);
        }}
      />

      {/* Create Catalogue Modal */}
      <HKPConfirmDialog
        open={confirmDiscardEdit}
        title="Discard changes?"
        description="Your unsaved catalogue changes will be lost."
        confirmLabel="Discard changes"
        onCancel={() => setConfirmDiscardEdit(false)}
        onConfirm={() => { setConfirmDiscardEdit(false); clearEdit(); }}
      />
      <HKPDrawer
        open={Boolean(editingCatalogue)}
        mode="edit"
        title="Edit catalogue"
        busy={editLoading}
        onClose={handleCancelEdit}
        footer={
          <div className="d-flex justify-content-end gap-2">
            <Button type="button" outline disabled={editLoading} onClick={handleCancelEdit}>Cancel</Button>
            <Button type="button" color="primary" disabled={editLoading} onClick={() => editingCatalogue && handleSaveEdit(editingCatalogue)}>
              {editLoading ? "Saving…" : "Save changes"}
            </Button>
          </div>
        }
      >
        <FormGroup>
          <Label for="catalogueEditName">Name</Label>
          <Input id="catalogueEditName" value={editName} onChange={(e) => { setEditName(e.target.value); setEditFieldErrors((previous) => ({ ...previous, name: "" })); }} invalid={Boolean(editFieldErrors.name)} autoFocus />
          {editFieldErrors.name && <FormFeedback>{editFieldErrors.name}</FormFeedback>}
        </FormGroup>
        <FormGroup>
          <Label for="catalogueEditDescription">Description</Label>
          <Input id="catalogueEditDescription" type="textarea" rows={3} value={editDescription} onChange={(e) => { setEditDescription(e.target.value); setEditFieldErrors((previous) => ({ ...previous, description: "" })); }} invalid={Boolean(editFieldErrors.description)} />
          {editFieldErrors.description && <FormFeedback>{editFieldErrors.description}</FormFeedback>}
        </FormGroup>
        <FormGroup>
          <Label for="catalogueEditCategory">Category</Label>
          <Input id="catalogueEditCategory" value={editCategory} onChange={(e) => { setEditCategory(e.target.value); setEditFieldErrors((previous) => ({ ...previous, category: "" })); }} invalid={Boolean(editFieldErrors.category)} />
          {editFieldErrors.category && <FormFeedback>{editFieldErrors.category}</FormFeedback>}
        </FormGroup>
        <FormGroup>
          <Label for="catalogueEditTargetMarket">Target market</Label>
          <Input id="catalogueEditTargetMarket" value={editTargetMarket} onChange={(e) => { setEditTargetMarket(e.target.value); setEditFieldErrors((previous) => ({ ...previous, targetMarket: "" })); }} invalid={Boolean(editFieldErrors.targetMarket)} />
          {editFieldErrors.targetMarket && <FormFeedback>{editFieldErrors.targetMarket}</FormFeedback>}
        </FormGroup>
        {editError && <p role="alert" className="text-danger">{editError}</p>}
      </HKPDrawer>
      <CatalogueModal
        openModal={createModalOpen}
        closeModal={handleCloseCreateModal}
        editData={null}
        getAllData={loadCatalogues}
        handleName={() => {}} // Not used in this context
      />

      {/* Share Modal */}
      {shareModalOpen && selectedCatalogueForShare && (
        <ShareInviteModal
          catalogue={selectedCatalogueForShare}
          isOpen={shareModalOpen}
          onClose={handleCloseShareModal}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && catalogueToDelete && (
        <DeleteConfirmationModal
          isOpen={deleteModalOpen}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          catalogueName={catalogueToDelete.name}
          loading={deleteLoading}
        />
      )}
    </div>
  );
};


export default connect(null, {
  getCatAlogue
})(CatalogueList);
